import { Button, cn } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'

/**
 * Which empty situation an {@link EmptyState} describes (TO-650, ux-interaction-standards §3).
 * Each variant has its own default copy and its own call-to-action rule.
 */
export enum EmptyStateVariant {
	/** First use: the collection has never held anything. The consumer's `action` is the CTA. */
	NO_DATA = 'no-data',
	/** The active filters exclude every item. The CTA is a built-in "Clear filters" button. */
	FILTERED_ZERO = 'filtered-zero',
	/** The viewer may not see this content. Never offers an action — it would fail for them. */
	NO_PERMISSION = 'no-permission',
}

/**
 * Built-in strings of {@link EmptyState}, overridable per locale (tech §59). The title and
 * description pairs are the variant default copy, used only when the consumer omits `title`.
 */
export interface EmptyStateLabels {
	noDataTitle: string
	noDataDescription: string
	filteredZeroTitle: string
	filteredZeroDescription: string
	clearFilters: string
	noPermissionTitle: string
	noPermissionDescription: string
}

const DEFAULT_LABELS: EmptyStateLabels = {
	noDataTitle: 'Nothing here yet',
	noDataDescription: 'Items you add will appear here.',
	filteredZeroTitle: 'No results',
	filteredZeroDescription: 'Nothing matches the current filters.',
	clearFilters: 'Clear filters',
	noPermissionTitle: "You don't have access",
	noPermissionDescription: 'Ask an administrator if you need access to this.',
}

/** The label keys holding each variant's default title and description. */
const VARIANT_COPY: Record<EmptyStateVariant, { title: keyof EmptyStateLabels; description: keyof EmptyStateLabels }> =
	{
		[EmptyStateVariant.NO_DATA]: { title: 'noDataTitle', description: 'noDataDescription' },
		[EmptyStateVariant.FILTERED_ZERO]: { title: 'filteredZeroTitle', description: 'filteredZeroDescription' },
		[EmptyStateVariant.NO_PERMISSION]: { title: 'noPermissionTitle', description: 'noPermissionDescription' },
	}

/** Optional call-to-action rendered as a primitives-layer Button. */
export interface EmptyStateAction {
	label: string
	onPress?: () => void
}

/**
 * Props for the {@link EmptyState} molecule.
 *
 * @property variant - The empty situation; defaults to `no-data` (see {@link EmptyStateVariant}).
 * @property title - Heading. When supplied the consumer owns the copy; when omitted the variant's
 *   default title and description render.
 * @property description - Optional supporting text; replaces the variant default description.
 * @property icon - Optional illustrative node, hidden from assistive tech.
 * @property action - Call-to-action button, rendered by the `no-data` variant only.
 * @property onClearFilters - Handler of the `filtered-zero` variant's "Clear filters" button; the
 *   button renders only when it is supplied.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 * @property className - Extra Tailwind classes merged onto the container.
 *
 * @example
 * ```tsx
 * <EmptyState variant={EmptyStateVariant.FILTERED_ZERO} onClearFilters={clearFilters} />
 * ```
 */
export interface EmptyStateProps {
	variant?: EmptyStateVariant
	title?: string
	description?: ReactNode
	icon?: ReactNode
	action?: EmptyStateAction
	onClearFilters?: () => void
	labels?: Partial<EmptyStateLabels>
	className?: string
}

/**
 * The heading and description to render. A supplied `title` means the consumer owns the copy, so
 * no default description is added beneath it — every pre-variant `<EmptyState title="…" />` call
 * renders exactly as before (design-system-core-components REQ-005).
 */
function resolveCopy(
	variant: EmptyStateVariant,
	text: EmptyStateLabels,
	title: string | undefined,
	description: ReactNode,
): { heading: string; body: ReactNode } {
	if (title !== undefined) return { heading: title, body: description }
	const keys = VARIANT_COPY[variant]
	return { heading: text[keys.title], body: description ?? text[keys.description] }
}

/** The variant's call to action (REQ-016): one button at most, never one for no-permission. */
function renderCallToAction(
	variant: EmptyStateVariant,
	text: EmptyStateLabels,
	action: EmptyStateAction | undefined,
	onClearFilters: (() => void) | undefined,
): ReactElement | null {
	if (variant === EmptyStateVariant.NO_PERMISSION) return null
	if (variant === EmptyStateVariant.FILTERED_ZERO) {
		if (!onClearFilters) return null
		return (
			<Button size="sm" variant="secondary" onPress={onClearFilters}>
				{text.clearFilters}
			</Button>
		)
	}
	if (!action) return null
	return (
		<Button size="sm" onPress={action.onPress}>
			{action.label}
		</Button>
	)
}

/**
 * Placeholder shown where a collection would be, when there is nothing to show.
 *
 * @remarks
 * - **Variants:** `no-data` (first use), `filtered-zero` (filters exclude everything) and
 *   `no-permission` (the viewer may not see it) each carry distinct default copy and CTA rules.
 *   `data-variant` on the container names the variant.
 * - **Prop-driven:** with a supplied `title`, each optional region renders only when its prop is
 *   supplied, so a bare `<EmptyState title="…" />` is a heading and nothing else.
 * - **Composition:** the CTA is the primitives-layer `<Button>`; the icon is whatever node the
 *   consumer passes, marked `aria-hidden` as decoration.
 * - **Styling:** every colour, radius, spacing and type class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/empty-state/empty-state.stories.tsx for visual examples
 */
export function EmptyState({
	variant = EmptyStateVariant.NO_DATA,
	title,
	description,
	icon,
	action,
	onClearFilters,
	labels,
	className,
}: EmptyStateProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	const { heading, body } = resolveCopy(variant, text, title, description)
	return (
		<div
			data-variant={variant}
			className={cn(
				'flex flex-col items-center gap-3 rounded-ds-lg border border-ds-border p-8 text-center',
				className,
			)}
		>
			{icon ? (
				<span data-testid="empty-state-icon" aria-hidden="true" className="text-ds-text-subtle">
					{icon}
				</span>
			) : null}
			<h3 className="text-ds-heading-3 text-ds-text">{heading}</h3>
			{body ? (
				<p data-testid="empty-state-description" className="text-ds-body text-ds-text-muted">
					{body}
				</p>
			) : null}
			{renderCallToAction(variant, text, action, onClearFilters)}
		</div>
	)
}
